import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI } from '../../lib/api';
import { Bell, Check, CheckCheck, Trash2, X, FileText, AlertCircle, Clock, FileCheck, FileSignature } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';

const ICON_MAP = {
  ocr_failed: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  sent_to_review: { icon: FileText, color: 'text-amber-500', bg: 'bg-amber-50' },
  payroll_packet_complete: { icon: FileCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  payroll_packet_failed: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  deliverable_uploaded: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  email_intake_processed: { icon: FileText, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  review_assigned: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' },
  review_approved: { icon: FileCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  review_rejected: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  review_queue_high: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  contract_expiring: { icon: FileSignature, color: 'text-orange-500', bg: 'bg-orange-50' },
};

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const NotificationCenter = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const tenantId = user?.tenant_id;

  const fetchCount = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await notificationsAPI.getCount(tenantId);
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error('Failed to fetch notification count', e);
    }
  }, [tenantId]);

  const fetchNotifications = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ tenant_id: tenantId, limit: 30 });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    await notificationsAPI.markRead(id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await notificationsAPI.markAllRead(tenantId);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (id) => {
    const n = notifications.find((n) => n.id === id);
    await notificationsAPI.delete(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (n && !n.is_read) setUnreadCount((c) => Math.max(0, c - 1));
  };

  const getIcon = (type) => {
    const config = ICON_MAP[type] || { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' };
    const Icon = config.icon;
    return (
      <div className={`p-2 rounded-lg ${config.bg} shrink-0`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
      </div>
    );
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notifications-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center animate-pulse"
            data-testid="notification-badge-count"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-12 w-[400px] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/70">
            <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={markAllRead} data-testid="mark-all-read-btn">
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* List */}
          <ScrollArea className="max-h-[420px]">
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 transition-colors group ${
                      n.is_read ? 'bg-white' : 'bg-indigo-50/40'
                    } hover:bg-slate-50`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    {getIcon(n.notification_type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                        {n.priority && (
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${PRIORITY_COLORS[n.priority] || ''}`}>
                            {n.priority}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {n.created_at && formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {!n.is_read && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => markRead(n.id)} title="Mark as read">
                          <Check className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteNotification(n.id)} title="Delete">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
